import { useState, useCallback } from 'react';
import { selectionToPrefab, usePrefabStore } from '../persistence/prefabStore';
import { useMapEditorStore } from '../store/mapEditorStore';

interface PrefabLibraryProps {
  /** Current floor index - newly dropped prefab instances land here. */
  currentFloor: number;
}

/**
 * Prefab Library panel - displayed in the right sidebar "Prefabs" tab.
 *
 * Features:
 * - Save current selection as a named prefab
 * - Drop a prefab into the scene at the last raycast hit position
 * - Delete a saved prefab
 */
export function PrefabLibrary({ currentFloor }: PrefabLibraryProps) {
  const prefabs = usePrefabStore((s) => s.prefabs);
  const savePrefab = usePrefabStore((s) => s.savePrefab);
  const deletePrefab = usePrefabStore((s) => s.deletePrefab);

  const selectedObjectIds = useMapEditorStore((s) => s.selectedObjectIds);
  const objectInstances = useMapEditorStore((s) => s.objectInstances);
  const lights = useMapEditorStore((s) => s.lights);
  const triggers = useMapEditorStore((s) => s.triggers);
  const decals = useMapEditorStore((s) => s.decals);
  const insertPrefabGroup = useMapEditorStore((s) => s.insertPrefabGroup);
  const selectObject = useMapEditorStore((s) => s.selectObject);
  const lastRaycastHit = useMapEditorStore((s) => s.lastRaycastHit);

  const [saveName, setSaveName] = useState('');

  const handleSave = useCallback(() => {
    if (selectedObjectIds.length === 0) return;
    // selectionToPrefab pulls all four entity kinds, not just objectInstances
    // - this used to filter objectInstances alone, so a selected light,
    // trigger or decal silently vanished from the saved prefab with no
    // error. See DockPrefabs (Studio's own "save selection" UI) for the
    // other caller sharing this same helper.
    const selection = selectionToPrefab({ objectInstances, lights, triggers, decals }, selectedObjectIds);
    savePrefab(saveName || `Prefab ${prefabs.length + 1}`, selection);
    setSaveName('');
  }, [decals, lights, objectInstances, prefabs.length, saveName, savePrefab, selectedObjectIds, triggers]);

  const handleDrop = useCallback((prefabId: string) => {
    const prefab = usePrefabStore.getState().prefabs.find((p) => p.id === prefabId);
    if (!prefab) return;
    const dropX = lastRaycastHit?.[0] ?? 0;
    const dropZ = lastRaycastHit?.[1] ?? 0;
    // insertPrefabGroup (the same path Studio's DockPrefabs drops through)
    // places every captured kind - instances, lights, triggers, decals -
    // not just instances, and mints fresh ids as one undo step.
    const newIds = insertPrefabGroup(
      { instances: prefab.instances, lights: prefab.lights, triggers: prefab.triggers, decals: prefab.decals },
      currentFloor,
      [dropX, dropZ],
      'Drop prefab',
    );
    if (newIds.length === 1) selectObject(newIds[0] ?? null);
  }, [currentFloor, insertPrefabGroup, lastRaycastHit, selectObject]);

  const selectedCount = selectedObjectIds.length;

  return (
    <div className="editor-prefab-library">
      <div className="editor-prefab-save-row">
        <input
          className="editor-prefab-name-input"
          placeholder="Prefab name…"
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          disabled={selectedCount === 0}
        />
        <button
          type="button"
          className="editor-chip"
          onClick={handleSave}
          disabled={selectedCount === 0}
          title={selectedCount === 0 ? 'Select objects first' : `Save ${selectedCount} object${selectedCount > 1 ? 's' : ''} as prefab`}
        >
          Save
        </button>
      </div>
      {selectedCount === 0 ? (
        <p className="editor-prefab-hint">Select one or more objects in the scene, then click Save.</p>
      ) : (
        <p className="editor-prefab-hint">{selectedCount} object{selectedCount > 1 ? 's' : ''} will be saved.</p>
      )}

      {prefabs.length === 0 ? (
        <div className="editor-prefab-empty">
          <p>No prefabs saved yet.</p>
        </div>
      ) : (
        <ul className="editor-prefab-list">
          {prefabs.map((prefab) => (
            <li key={prefab.id} className="editor-prefab-item">
              <div className="editor-prefab-info">
                <span className="editor-prefab-name">{prefab.name}</span>
                <span className="editor-prefab-meta">
                  {prefab.instances.length} obj{prefab.instances.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="editor-prefab-actions">
                <button
                  type="button"
                  className="editor-chip"
                  onClick={() => handleDrop(prefab.id)}
                  title="Drop prefab at cursor position"
                >
                  Drop
                </button>
                <button
                  type="button"
                  className="editor-chip is-danger"
                  onClick={() => {
                    // Every other destructive action reviewed alongside this
                    // one routes through a confirm step first (Studio's own
                    // EdConfirmDialog, unreachable here - this file must stay
                    // inside the engine boundary, see
                    // scripts/check-engine-boundary.mjs); window.confirm is
                    // already this same boundary's own established pattern
                    // for exactly this (CharacterStudio.tsx's discard-pose
                    // guard) rather than a plain instant delete with no
                    // confirmation at all.
                    if (window.confirm(`Delete the "${prefab.name}" prefab? This can't be undone.`)) {
                      deletePrefab(prefab.id);
                    }
                  }}
                  title="Delete this prefab"
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
